import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    Form,
    Input,
    Modal,
    Select,
    DatePicker,
    TimePicker,
    InputNumber,
    Skeleton,
    Drawer,
    Space,
    Image,
    message,
    Tag,
} from 'antd';
import {CloseOutlined, PlusOutlined, RobotOutlined} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {PickupAppointmentDrawerProps} from '../types/PickupAppointmentType';
import {usePickupAppointmentDrawer} from '../hooks/usePickupAppointmentDrawer';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { usePickupStartPartialGray } from '../../../../hooks/usePickupStartPartialGray';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import {useCooperationWarehouseSearch} from '../hooks/useCooperationWarehouseSearch';
import type { CooperationWarehouseForAppointment } from '../graphql/queries/getCooperationWarehouseQueries';
import { useLazyQuery } from '@apollo/client';
import {
    GET_COOPERATION_WAREHOUSE_FOR_APPOINTMENT,
    GetCooperationWarehouseByIdResponse,
    GetCooperationWarehouseByIdVariables
} from '../graphql/queries/getCooperationWarehouseQueries';
import {
    RESOLVE_PICKUP_SITE_VEHICLE_TYPE,
    ResolvePickupSiteVehicleTypeResponse
} from '../graphql/queries/resolvePickupSiteVehicleType';
import { useVehicleTypeEnOptions } from '../../../../hooks/useVehicleTypeOptions';
import {
    statusOptions,
    timezoneOptions,
    packageUnitFilterOptions,
    getWeekdaysOptions,
    manualReasonOptions,
    DEFAULT_MANUAL_REASON
} from '../types/PickupAppointmentType';
import MultiDateSelector from '../components/MultiDateSelector';
import { useTranslation } from 'react-i18next';
import { useStatusInternationalization } from 'pages/delivery/parcels/hooks/useStatusInternationalization';
import ImageUploader from '../../../../components/ImageUploader';
import ImageUploaderEnhance from './ImageUploaderEnhance';

const {Option} = Select;
const {TextArea} = Input;

const PickupAppointmentDrawer: React.FC<PickupAppointmentDrawerProps> = (props) => {
    const {
        form,
        state,
        channelData,
        channelLoading,
        exceptionReasonsData,
        exceptionReasonsLoading,
        queryLoading,
        appointmentData,
        warehouseQueryLoading,
        minimumPickupDate,
        canCreateAppointment,
        handleStateChange,
        handleCityChange,
        handleZipcodeChange,
        handleSubmit,
        handleCancelAppointment,
        handleConfirmDrawerCancel,
        cancelDrawerModalVisible,
        setCancelDrawerModalVisible,
        cancelDrawerVoucherS3Keys,
        setCancelDrawerVoucherS3Keys,
        cancelDrawerVoucherError,
        setCancelDrawerVoucherError,
        cancelLoading,
        isCancelable,
        getFieldEditability,
        getTitle,
        getChannelDisplayName,
        getWarehouseDisplayName
    } = usePickupAppointmentDrawer(props);

    const {visible, onClose, mode, initialCooperationWarehouseId, initialChannelId, initialManualReason, reviewConfirmMode, reviewConfirmLoading, onReviewConfirm} = props;
    const {loading, appointment} = state;
    const { currentWarehouse: warehouse } = useWarehouseContext();
    const { startPartialEnabled } = usePickupStartPartialGray();
    const navigate = useNavigate();
    const {
        options: cooperationWarehouseOptions,
        loading: cooperationWarehouseLoading,
        search: searchCooperationWarehouses,
        reset: resetCooperationWarehouseSearch,
        getWarehouseById
    } = useCooperationWarehouseSearch();

    // 当前合作仓库信息状态
    const [currentCooperationWarehouse, setCurrentCooperationWarehouse] = useState<CooperationWarehouseForAppointment | null>(null);
    // 标记本次 getCooperationWarehouseById 是复揽预填触发，加载完需全量回填地址等字段
    const prefillSiteRef = useRef(false);
    const {t} = useTranslation();
    const { getExceptionReasonText } = useStatusInternationalization();
    // 渠道搜索状态
    const [channelSearchText, setChannelSearchText] = useState<string>('');

    // 根据ID查询合作仓库的GraphQL查询
    const [getCooperationWarehouseById] = useLazyQuery<
        GetCooperationWarehouseByIdResponse,
        GetCooperationWarehouseByIdVariables
    >(GET_COOPERATION_WAREHOUSE_FOR_APPOINTMENT, {
        onCompleted: (data) => {
            // 查询根字段是 getCooperationWarehouseForAppointment，勿读成 getCooperationWarehouseById（曾致预填全空）
            const w = data.getCooperationWarehouseForAppointment;
            if (w) {
                console.log('Pickup Site loaded:', w);
                setCurrentCooperationWarehouse(w);
                // 强制更新表单显示，确保Select组件重新渲染
                form.setFieldValue('cooperationWarehouseId', w.cooperationWarehouseId);
                // 复揽预填：创建模式下把站点联系人/地址/工作时间等一并回填（等同手动选择站点，含备注）。
                // 详情模式不在此覆盖 notes：预约单应展示自身备注，而非站点当前备注。
                if (prefillSiteRef.current) {
                    applyCooperationWarehouseFields(w);
                    prefillSiteRef.current = false;
                }
            }
        },
        onError: (error) => {
            console.error('Error fetching Pickup Site by ID:', error);
            setCurrentCooperationWarehouse(null);
        }
    });

    // 车型默认值：站点 + 期望揽收日期变化时向后端求当日排期车型（前端不自行解析排期 JSON）。
    // 用户手动选过就不再覆盖，否则运营改完日期会发现自己选的车型被悄悄改掉。
    const vehicleTypeTouchedRef = useRef(false);
    const vehicleTypeOptions = useVehicleTypeEnOptions();
    const [resolveSiteVehicleType] = useLazyQuery<ResolvePickupSiteVehicleTypeResponse>(
        RESOLVE_PICKUP_SITE_VEHICLE_TYPE,
        { fetchPolicy: 'network-only' }
    );

    // 请求序号：运营 1 秒内连改站点会并发多个请求，先发的可能后到。
    // 不做防护就会出现「站点是 B、车型却回填成 A 的」——运营本来就不知道 B 该是什么车型，
    // 这个错配在页面上看不出来，会一路带到排线分组里。只认最后一次请求的响应。
    const vehicleTypeReqSeqRef = useRef(0);

    const refreshDefaultVehicleType = (cooperationWarehouseId?: number, expectedPickupDate?: any) => {
        if (vehicleTypeTouchedRef.current) return;
        const rawCwId = cooperationWarehouseId ?? form.getFieldValue('cooperationWarehouseId');
        const rawDate = expectedPickupDate ?? form.getFieldValue('expectedPickupDate');
        // 必须 Number 化后再判，不能只判真值：站点那个 Form.Item 绑的是 <Space.Compact>，
        // antd 会把注入的 onChange 透传到包裹 div 上，Select 内部搜索框的 change 事件冒泡上去，
        // 被默认的 getValueFromEvent 取成 e.target.value —— 于是运营每敲一个字，
        // 本字段就被写成搜索词（"a"）而不是站点 id。原来的 `if (!cwId)` 放行了它，
        // 结果把 "a" 当 Long! 发给 resolvePickupSiteVehicleType，页面弹 GraphQL 类型错。
        const cwId = Number(rawCwId);
        if (!Number.isFinite(cwId) || cwId <= 0) return;
        const day = dayjs(rawDate);
        if (!rawDate || !day.isValid()) return;
        const seq = ++vehicleTypeReqSeqRef.current;
        resolveSiteVehicleType({
            variables: {
                cooperationWarehouseId: cwId,
                expectedPickupDate: day.format('YYYY-MM-DD')
            }
        })
            .then((res) => {
                if (seq !== vehicleTypeReqSeqRef.current) return;
                if (vehicleTypeTouchedRef.current) return;
                // 取默认值失败不该挡住建单：留空即可，后端在展示/筛选/导出时仍会回退站点排期
                if (res?.error) return;
                form.setFieldValue('vehicleTypeId', res?.data?.resolvePickupSiteVehicleType ?? undefined);
            })
            .catch(() => undefined);
    };

    // 抽屉每次打开都重置「用户手动选过」标记，避免上一单的选择影响下一单的默认值
    useEffect(() => {
        if (visible) vehicleTypeTouchedRef.current = false;
    }, [visible]);

    // 详情/编辑：单上没存车型（存量单）时同样带出站点当日车型，避免列表显示有值、编辑框却空白
    useEffect(() => {
        if (!visible || mode === 'create') return;
        const detail = appointmentData?.getPickupAppointmentDetail;
        if (!detail || detail.vehicleTypeId != null) return;
        refreshDefaultVehicleType(
            detail.cooperationWarehouseId ? Number(detail.cooperationWarehouseId) : undefined,
            detail.expectedPickupDate
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, mode, appointmentData]);

    // 用一个合作仓库对象回填联系人/地址/工作时间等表单字段（手动选择与复揽预填共用）
    const applyCooperationWarehouseFields = (w: CooperationWarehouseForAppointment) => {
        form.setFieldsValue({
            cooperationWarehouseId: w.cooperationWarehouseId,
            contactName: w.contactName || '',
            contactPhone: w.contactPhone || '',
            countryCode: w.countryCode || 1,
            contactEmail: w.contactEmail || '',
            ext: w.ext || '',
            addressLine1: w.addressLine1 || '',
            addressLine2: w.addressLine2 || '',
            country: w.country || 'US',
            state: w.state || '',
            city: w.city || '',
            zipcode: w.zipcode || '',
            workWeekdaysJson: w.workWeekdaysJson || [1, 2, 3, 4, 5],
            workStartTime: w.workStartTime ? dayjs(w.workStartTime, 'HH:mm') : dayjs().hour(9).minute(0),
            workEndTime: w.workEndTime ? dayjs(w.workEndTime, 'HH:mm') : dayjs().hour(18).minute(0),
            notes: w.remark || ''
        });
        // 车型默认值必须在这里显式重取，不能指望 Form 的 onValuesChange：
        // 站点那个 Select 受控于自身 onChange（外层 Form.Item 的注入 onChange 被
        // <Space.Compact> 透传到了包裹 div 上），站点值是靠上面这句 setFieldsValue 写进去的，
        // 而 setFieldsValue **不触发 onValuesChange** —— 于是「站点变了就重取车型」那个分支
        // 永远不会因选站点而执行，表现为选完站点车型框一直是空的。
        // 传 id 而不依赖 form：避免受 setFieldsValue 的生效时机影响。
        refreshDefaultVehicleType(w.cooperationWarehouseId);
    };

    // 处理合作仓库选择，自动填充相关字段
    const handleCooperationWarehouseChange = (cooperationWarehouseId: number) => {
        const selectedWarehouse = getWarehouseById(cooperationWarehouseId);
        if (selectedWarehouse) {
            // 换站点必须清掉「用户手选过车型」的标记，否则新站点带不出车型。
            // vehicleTypeTouchedRef 的本意是防「改日期把手选车型冲掉」（站点没变，选车依据还在），
            // 换站点则是依据本身变了：手选的车是针对老站点做的判断，对新站点不成立。
            // 也与本函数的既有行为一致——联系人、地址、工作时间、备注在换站点时同样被无条件重写。
            vehicleTypeTouchedRef.current = false;
            applyCooperationWarehouseFields(selectedWarehouse);
        }
    };

    // 跳转到合作仓库页面并打开新增抽屉
    const handleJumpToCooperationWarehouse = () => {
        navigate('/pickup/cooperation-warehouse?action=create');
    };

    // 处理渠道搜索
    const handleChannelSearch = (value: string) => {
        setChannelSearchText(value);
    };

    // 过滤渠道列表
    const filteredChannels = React.useMemo(() => {
        if (!channelData?.getAllChannelsLite) return [];
        
        if (!channelSearchText.trim()) {
            return channelData.getAllChannelsLite;
        }
        
        return channelData.getAllChannelsLite.filter(channel => 
            channel.channelName.toLowerCase().includes(channelSearchText.toLowerCase())
        );
    }, [channelData?.getAllChannelsLite, channelSearchText]);

    // 当 Drawer 打开时重置搜索状态
    useEffect(() => {
        if (visible) {
            resetCooperationWarehouseSearch();
        }
    }, [visible, resetCooperationWarehouseSearch]);

    // 新建(无复揽预填)打开时清掉上次残留的站点对象，保证是干净抽屉
    useEffect(() => {
        if (visible && mode === 'create' && !initialCooperationWarehouseId) {
            setCurrentCooperationWarehouse(null);
        }
    }, [visible, mode, initialCooperationWarehouseId]);

    // 当获取到详情数据且有合作仓库ID时，自动加载合作仓库信息
    useEffect(() => {
        const cooperationWarehouseId = appointmentData?.getPickupAppointmentDetail?.cooperationWarehouseId;
        if (cooperationWarehouseId && mode !== 'create') {
            console.log('Loading Pickup Site by ID:', cooperationWarehouseId);
            getCooperationWarehouseById({
                variables: { cooperationWarehouseId }
            });
        }
    }, [appointmentData?.getPickupAppointmentDetail?.cooperationWarehouseId, getCooperationWarehouseById, mode]);

    // 复揽跳转预填：创建模式下按 initialCooperationWarehouseId 加载站点并全量回填
    useEffect(() => {
        if (mode === 'create' && visible && initialCooperationWarehouseId) {
            prefillSiteRef.current = true;
            getCooperationWarehouseById({
                variables: { cooperationWarehouseId: initialCooperationWarehouseId }
            });
        }
    }, [mode, visible, initialCooperationWarehouseId, getCooperationWarehouseById]);

    // 复揽预填「补写」：create 初始化 effect 依赖 minimumPickupDate（异步 set），
    // 会在预填后再次 resetFields 把站点冲掉。此 effect 与其同源触发、且在其之后执行，
    // 站点被清空时重新回填（守卫：字段已是目标站点则不覆盖用户手改）。
    useEffect(() => {
        // 站点已加载(currentCooperationWarehouse)且表单站点字段为空(被 resetFields 清掉)时补回。
        // 不再要求 id 精确相等，避免任何取值/类型差异导致漏补。
        if (mode === 'create' && visible && initialCooperationWarehouseId
            && currentCooperationWarehouse
            && !form.getFieldValue('cooperationWarehouseId')) {
            applyCooperationWarehouseFields(currentCooperationWarehouse);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, visible, initialCooperationWarehouseId, currentCooperationWarehouse, minimumPickupDate, warehouse]);

    // 复揽跳转预填渠道：与站点同源，create 初始化 effect 的 resetFields 会清掉，
    // 故同样在其后补写（字段为空才补，避免覆盖用户手改）。
    useEffect(() => {
        if (mode === 'create' && visible && initialChannelId
            && !form.getFieldValue('channelId')) {
            form.setFieldValue('channelId', initialChannelId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, visible, initialChannelId, currentCooperationWarehouse, minimumPickupDate, warehouse]);

    // 确保 minimumPickupDate 有默认值
    const safeMinimumPickupDate = minimumPickupDate || dayjs();

    if (!visible) return null;

    if (queryLoading || !safeMinimumPickupDate) {
        return (
            <Drawer
                className="[&_.ant-form-item-label]:!text-left [&_.ant-form-item-label>label]:!justify-start [&_.ant-form-item-label>label]:!w-full"
                title={getTitle()}
                width={650}
                placement="right"
                closable={false}
                onClose={onClose}
                open={visible}
            >
                <Skeleton active/>
            </Drawer>
        );
    }
    return (
        <>
        <Drawer
            className="[&_.ant-form-item-label]:!text-left [&_.ant-form-item-label>label]:!justify-start [&_.ant-form-item-label>label]:!w-full"
            title={
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <span className="h-5 border-l-4 border-purple-500 mr-2 mt-1"></span>
                        <span className="text-xl font-medium text-black">
                            {reviewConfirmMode ? t('pickup.addressReviewPool.appointment.confirmDrawerTitle') : getTitle()}
                        </span>
                        {reviewConfirmMode && (
                            <span className="ml-3 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-bold text-teal-700">
                                {t('pickup.addressReviewPool.appointment.existing')}
                            </span>
                        )}
                        {appointment && mode !== 'create' && (
                            <span className="ml-4 text-gray-500">#{appointment.appointmentNumber}</span>
                        )}
                    </div>
                    <CloseOutlined onClick={onClose} className="cursor-pointer"/>
                </div>
            }
            width={650}
            placement="right"
            closable={false}
            onClose={onClose}
            open={visible}
            footer={
                reviewConfirmMode ? (
                    // 审核池「确认预约单」：只读态，footer 换成 关闭 + 确认当日预约单（→ resolveReviewPoolAsConfirmAppointment）。
                    <div className="flex justify-end items-center space-x-2">
                        <Button size="large" onClick={onClose}>
                            {t('pickup.addressReviewPool.appointment.close')}
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            loading={reviewConfirmLoading}
                            onClick={() => onReviewConfirm?.()}
                        >
                            {t('pickup.addressReviewPool.appointment.confirmTodayCta')}
                        </Button>
                    </div>
                ) : (
                <div className="flex justify-between items-center">
                    {/* 只有在详情模式且状态可取消时才显示取消预约按钮 */}
                    {mode === 'detail' && isCancelable(appointment?.status) ? (
                        <Button
                            type="primary"
                            size="large"
                            loading={loading}
                            onClick={handleCancelAppointment}
                        >
                            {t('pickup.appointment.appointmentPageDetails.buttons.cancelAppointment')}
                        </Button>
                    ) : <div></div>}
                    <div className="flex space-x-2">
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => form.submit()}
                            loading={loading}
                            disabled={mode === 'create' && !canCreateAppointment}
                            title={mode === 'create' && !canCreateAppointment ? 'Current time has passed the appointment deadline' : ''}
                        >
                            {mode === 'detail' ? t('pickup.appointment.appointmentPageDetails.buttons.update') : t('pickup.appointment.appointmentPageDetails.buttons.ok')}
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            onClick={onClose}
                        >
                            {t('pickup.appointment.appointmentPageDetails.buttons.cancel')}
                        </Button>
                    </div>
                </div>
                )
            }
        >
            <div className="p-6">


                <Form
                    form={form}
                    layout="horizontal"
                    labelAlign="left"
                    labelCol={{span: 6}}
                    wrapperCol={{span: 18}}
                    labelWrap
                    onFinish={handleSubmit}
                    onValuesChange={(changed) => {
                        // 站点或期望揽收日期变了，车型默认值要跟着重取（用户手动选过则不动）
                        if ('cooperationWarehouseId' in changed || 'expectedPickupDate' in changed) {
                            refreshDefaultVehicleType();
                        }
                    }}
                >
                    {/* Hidden fields to ensure they're included in form submission */}
                    <Form.Item name="appointmentNumber" hidden>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="isAbnormal" hidden>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="photosS3KeysJson" hidden>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="exceptionPhotosS3KeysJson" hidden>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="cancelVoucherS3Keys" hidden>
                        <Input/>
                    </Form.Item>


                    <Form.Item
                        name="channelId"
                        label={t('pickup.appointment.appointmentPageDetails.fields.customer')}
                        rules={[{required: true, message: 'Please select the channel.'}]}
                        initialValue={null}
                    >
                        {getFieldEditability('channelId') ? (
                            <Select
                                placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.searchCustomer')}
                                loading={channelLoading}
                                notFoundContent={channelLoading ? 'Loading...' : 'No Customer'}
                                showSearch
                                filterOption={false}
                                onSearch={handleChannelSearch}
                                allowClear
                                onClear={() => setChannelSearchText('')}
                            >
                                {filteredChannels.map((channel) => (
                                    <Option key={channel.id} value={parseInt(channel.id)}>
                                        {channel.channelName}
                                    </Option>
                                ))}
                            </Select>
                        ) : (
                            <Form.Item shouldUpdate noStyle>
                                {({ getFieldValue }) => (
                                    <Input
                                        value={getChannelDisplayName(getFieldValue('channelId'))}
                                        disabled
                                    />
                                )}
                            </Form.Item>
                        )}
                    </Form.Item>

                    <Form.Item
                        name="cooperationWarehouseId"
                        label={
                            <span className="whitespace-normal break-words text-sm text-gray-800">
                              {t('pickup.appointment.appointmentPageDetails.fields.pickupSite')}
                            </span>
                        }
                        rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectPickupSite') as string}]}
                        initialValue={null}
                    >
                        {getFieldEditability('cooperationWarehouseId') ? (
                            <Space.Compact style={{ display: 'flex', width: '100%' }}>
                                <Form.Item shouldUpdate noStyle style={{ flex: 1 }}>
                                    {({ getFieldValue }) => {
                                        const cooperationWarehouseId = getFieldValue('cooperationWarehouseId');

                                        // 构建显示选项列表
                                        let displayOptions: CooperationWarehouseForAppointment[] = [];

                                        if (cooperationWarehouseOptions.length > 0) {
                                            // 如果有搜索结果，使用搜索结果（完全覆盖）
                                            displayOptions = cooperationWarehouseOptions;
                                        } else if (currentCooperationWarehouse) {
                                            // 如果没有搜索结果，但有根据ID查询到的当前仓库，显示当前仓库
                                            displayOptions = [currentCooperationWarehouse];
                                        } else if (appointmentData?.getPickupAppointmentDetail?.cooperationWarehouseId &&
                                                  appointmentData?.getPickupAppointmentDetail?.cooperationWarehouseName) {
                                            // 如果没有其他数据源，但详情数据中有合作仓库信息，构造一个临时选项
                                            displayOptions = [{
                                                cooperationWarehouseId: parseInt(appointmentData.getPickupAppointmentDetail.cooperationWarehouseId.toString()),
                                                cooperationWarehouseName: appointmentData.getPickupAppointmentDetail.cooperationWarehouseName,
                                                countryCode: 1,
                                                addressLine1: '',
                                                country: 'US',
                                                workWeekdaysJson: [1, 2, 3, 4, 5],
                                                workStartTime: '09:00',
                                                workEndTime: '18:00'
                                            } as CooperationWarehouseForAppointment];
                                        }

                                        // 确保当前值在选项中存在时才设置value，否则让Select显示placeholder
                                        const hasValidOption = displayOptions.some(w => w.cooperationWarehouseId === cooperationWarehouseId);
                                        const selectValue = hasValidOption ? cooperationWarehouseId : undefined;

                                        return (
                                            <Select
                                                value={selectValue}
                                                placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.searchPickupSite') as string}
                                                showSearch
                                                filterOption={false}
                                                loading={cooperationWarehouseLoading}
                                                notFoundContent={cooperationWarehouseLoading ? 'Loading...' : 'No data'}
                                                onSearch={searchCooperationWarehouses}
                                                onSelect={handleCooperationWarehouseChange}
                                                autoFocus={false}
                                                defaultActiveFirstOption={false}
                                                allowClear
                                                onClear={() => {
                                                    resetCooperationWarehouseSearch();
                                                    form.setFieldValue('notes', '');
                                                }}
                                                onChange={handleCooperationWarehouseChange}
                                                style={{ flex: 1 }}
                                            >
                                                {displayOptions.map((warehouse) => (
                                                    <Option key={warehouse.cooperationWarehouseId} value={warehouse.cooperationWarehouseId}>
                                                        {warehouse.cooperationWarehouseName}
                                                    </Option>
                                                ))}
                                            </Select>
                                        );
                                    }}
                                </Form.Item>
                                {hasResource([ResourceCode.menu.PICKUP_SITE]) && (
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={handleJumpToCooperationWarehouse}
                                        title="Add new Pickup Site"
                                        style={{ flexShrink: 0 }}
                                    >
                                        {t('pickup.appointment.appointmentPageDetails.buttons.add')}
                                    </Button>
                                )}
                            </Space.Compact>
                        ) : (
                            <Form.Item shouldUpdate={() => true} noStyle>
                                {({ getFieldValue }) => {
                                    const cooperationWarehouseId = getFieldValue('cooperationWarehouseId');
                                    
                                    // 优先显示根据ID查询到的仓库名称
                                    let displayName = '';
                                    if (currentCooperationWarehouse && currentCooperationWarehouse.cooperationWarehouseId === cooperationWarehouseId) {
                                        displayName = currentCooperationWarehouse.cooperationWarehouseName;
                                    } else if (appointmentData?.getPickupAppointmentDetail?.cooperationWarehouseName) {
                                        // 如果当前合作仓库信息还没加载完成，先显示详情数据中的名称
                                        displayName = appointmentData.getPickupAppointmentDetail.cooperationWarehouseName;
                                    } else {
                                        // 如果没有根据ID查询到的信息，尝试从搜索选项中查找
                                        const selectedWarehouse = cooperationWarehouseOptions.find(
                                            (w: any) => w.cooperationWarehouseId === cooperationWarehouseId
                                        );
                                        displayName = selectedWarehouse?.cooperationWarehouseName || '';
                                    }
                                    
                                    return (
                                        <Input
                                            value={displayName}
                                            disabled
                                            placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.noPickupSiteSelected') as string}
                                        />
                                    );
                                }}
                            </Form.Item>
                        )}
                    </Form.Item>

                    <Form.Item
                        name="contactName"
                        label={t('pickup.appointment.appointmentPageDetails.fields.contactName')}
                        rules={[{required: true, message: t('pickup.appointment.messages.pleaseEnterTheContactName') as string}]}
                        initialValue=""
                    >
                        <Input placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.contactName') as string} disabled/>
                    </Form.Item>

                    {/* 电话输入 - 分为三部分 */}
                    <Form.Item
                        label=" "
                        colon={false}
                    >
                        <Space.Compact block>
                            <Form.Item
                                name="countryCode"
                                noStyle
                                initialValue={1}
                            >
                                <Select style={{width: '20%'}} placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.countryCode') as string} disabled>
                                    <Option value={1}>+1</Option>
                                    <Option value={86}>+86</Option>
                                </Select>
                            </Form.Item>
                            <Form.Item
                                name="contactPhone"
                                noStyle
                                initialValue=""
                                rules={[
                                    { required: true, message: t('pickup.appointment.messages.pleaseEnterPhoneNumber') as string },
                                    {
                                        pattern: /^\d{7,12}$/,
                                        message: t('pickup.appointment.messages.phoneNumberMustBe7To12Digits') as string,
                                    },
                                ]}
                            >
                                <Input style={{ width: '50%' }} placeholder="2345678912" disabled />
                            </Form.Item>
                            <Input
                                style={{width: '15%', textAlign: 'center'}}
                                placeholder={t('delivery.parcels.label.ext') as string}
                                disabled
                                className="bg-gray-50"
                            />
                            <Form.Item
                                name="ext"
                                noStyle
                                initialValue=""
                                rules={[
                                    {
                                        pattern: /^\d{1,6}$/, // 1~6 位数字，不要求0开头
                                        message: t('pickup.appointment.messages.pleaseEnter1To6DigitsOnly') as string,
                                    },
                                ]}
                            >
                                <Input style={{ width: '15%' }} placeholder={t('delivery.parcels.label.ext') as string} disabled />
                            </Form.Item>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item
                        name="contactEmail"
                        label=" "
                        colon={false}
                        rules={[{type: 'email', message: t('pickup.appointment.messages.pleaseEnterValidEmailAddress') as string}]}
                        initialValue=""
                    >
                        <Input placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.emailOptional') as string} disabled/>
                    </Form.Item>

                    <Form.Item
                        name="addressLine1"
                        label={t('pickup.appointment.appointmentPageDetails.fields.pickupAddress')}
                        rules={[{required: true, message: t('pickup.appointment.messages.pleaseEnterTheAddressLine1') as string}]}
                        initialValue=""
                    >
                        <Input placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.addressLine1') as string} disabled/>
                    </Form.Item>

                    <Form.Item
                        name="addressLine2"
                        label=" "
                        colon={false}
                        initialValue=""
                    >
                        <Input placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.addressLine2') as string} disabled/>
                    </Form.Item>

                    {/* 地址选择器 - 四级联查 */}
                    <Form.Item
                        label=" "
                        colon={false}
                    >
                        <Space.Compact block>
                            <Form.Item
                                name="country"
                                noStyle
                                rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectCountry') as string}]}
                                initialValue="US"
                            >
                                <Select style={{width: '20%'}} placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.country') as string} disabled>
                                    <Option value="US">US</Option>
                                </Select>
                            </Form.Item>
                            <Form.Item
                                name="state"
                                noStyle
                                rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectState') as string}]}
                                initialValue=""
                            >
                                <Select
                                    style={{width: '20%'}}
                                    placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.state') as string}
                                    loading={state.statesLoading}
                                    value={state.selectedState}
                                    disabled
                                >
                                    {state.states.map((stateItem) => (
                                        <Option key={stateItem} value={stateItem}>
                                            {stateItem}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                name="city"
                                noStyle
                                rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectCity') as string}]}
                                initialValue=""
                            >
                                <Select
                                    style={{width: '35%'}}
                                    placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.searchCity') as string}
                                    loading={state.citiesLoading}
                                    value={state.selectedCity}
                                    disabled
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) => {
                                        const children = option?.children as unknown as string;
                                        return children?.toLowerCase?.()?.indexOf(input.toLowerCase()) >= 0 || false;
                                    }}
                                    searchValue={undefined}
                                    showArrow={true}
                                >
                                    {state.cities.map((city) => (
                                        <Option key={city} value={city}>
                                            {city}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                name="zipcode"
                                noStyle
                                rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectZipcode') as string}]}
                                initialValue=""
                            >
                                <Select
                                    style={{width: '25%'}}
                                    placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.zipcode') as string}
                                    loading={state.zipcodesLoading || warehouseQueryLoading}
                                    value={state.selectedZipcode}
                                    disabled
                                    showSearch
                                    filterOption={(input, option) => {
                                        const children = option?.children as unknown as string;
                                        return children?.toLowerCase?.()?.includes(input.toLowerCase()) || false;
                                    }}
                                >
                                    {state.zipcodes.map((zipcode) => (
                                        <Option key={zipcode} value={zipcode}>
                                            {zipcode}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Space.Compact>
                    </Form.Item>

                    {/* 运营时间 - 三个组件一行 */}
                    <Form.Item
                        name="workWeekdaysJson"
                        label={t('pickup.appointment.appointmentPageDetails.fields.workDays')}
                        rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectWorkDays') as string}]}
                        initialValue={[1, 2, 3, 4, 5]} // 默认周一到周五
                    >
                        <Select
                            mode="multiple"
                            placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.selectWorkDays') as string}
                            style={{width: '100%'}}
                            disabled
                        >
                            {getWeekdaysOptions(t).map((option) => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label={t('pickup.appointment.appointmentPageDetails.fields.workTime')}>
                        <Space.Compact block>
                            <Form.Item
                                name="workStartTime"
                                noStyle
                                rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectStartTime') as string}]}
                                initialValue={dayjs().hour(9).minute(0).second(0)}
                            >
                                <TimePicker
                                    style={{width: '30%'}}
                                    format="HH:mm"
                                    disabled
                                />
                            </Form.Item>
                            <Input
                                style={{width: '10%', textAlign: 'center'}}
                                placeholder="~"
                                disabled
                                className="bg-gray-50"
                            />
                            <Form.Item
                                name="workEndTime"
                                noStyle
                                rules={[
                                    {required: true, message: t('pickup.appointment.messages.pleaseSelectEndTime') as string},
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const startTime = getFieldValue('workStartTime');
                                            if (!value || !startTime) {
                                                return Promise.resolve();
                                            }
                                            if (value.isAfter(startTime)) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Work end time must be after start time')); 
                                        },
                                    }),
                                ]}
                                initialValue={dayjs().hour(18).minute(0).second(0)}
                            >
                                <TimePicker style={{width: '30%'}} format="HH:mm" disabled/>
                            </Form.Item>
                            <Form.Item
                                name="timezone"
                                noStyle
                                rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectTimeZone') as string}]}
                                initialValue={mode === 'create' ? (warehouse?.timezone) : undefined}
                            >
                                {getFieldEditability('timezone') ? (
                                    <Select
                                        style={{width: '30%'}}
                                        placeholder="zone"
                                        showSearch
                                        filterOption={(input, option) => {
                                            const children = option?.children as unknown as string;
                                            return children?.toLowerCase?.()?.includes(input.toLowerCase()) || false;
                                        }}
                                        disabled
                                    >
                                        {timezoneOptions.map((option) => (
                                            <Option key={option.value} value={option.value}>
                                                {option.value}
                                            </Option>
                                        ))}
                                    </Select>
                                ) : (
                                    <Select
                                        style={{width: '30%'}}
                                        placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.zone') as string}
                                        disabled
                                        showSearch
                                        filterOption={(input, option) => {
                                            const children = option?.children as unknown as string;
                                            return children?.toLowerCase?.()?.includes(input.toLowerCase()) || false;
                                        }}
                                    >
                                        {timezoneOptions.map((option) => (
                                            <Option key={option.value} value={option.value}>
                                                {option.value}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item
                        name="warehouseId"
                        label={t('pickup.appointment.appointmentPageDetails.fields.hub')}
                        rules={[{required: true, message: 'Please select the pickup warehouse.'}]}
                    >
                        {/* 仓库现在显示为只读，显示当前选中的仓库 */}
                        <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue }) => (
                                <Input
                                    value={getWarehouseDisplayName(getFieldValue('warehouseId'))}
                                    disabled
                                    placeholder="Current warehouse"
                                />
                            )}
                        </Form.Item>
                    </Form.Item>

                    <Form.Item
                        name="expectedPickupDate"
                        label={t('pickup.appointment.appointmentPageDetails.fields.pickupDate')}
                        rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectPickupDate') as string}]}
                        initialValue={safeMinimumPickupDate}
                    >
                        <DatePicker
                            className="w-full"
                            format="YYYY/MM/DD"
                            disabled={true}
                            disabledDate={(current) => {
                                // 在新增模式下，禁用最小日期之前的日期
                                if (mode === 'create') {
                                    return current && current < safeMinimumPickupDate.startOf('day');
                                }
                                return false;
                            }}
                        />
                    </Form.Item>

                    {/* 期望揽件时间 - 两个时间选择器 */}
                    <Form.Item
                        label={
                            <span className="whitespace-normal break-words text-sm text-gray-800">
                          {t('pickup.appointment.appointmentPageDetails.fields.expectedPickupTime')}
                        </span>
                        }
                    >
                        <Space.Compact block>
                            <Form.Item
                                name="expectedPickupTimeStart"
                                noStyle
                                rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectStartTime') as string}]}
                                initialValue={dayjs().hour(9).minute(0).second(0)}
                            >
                                {getFieldEditability('expectedPickupTimeStart') ? (
                                    <TimePicker 
                                        style={{width: '45%'}} 
                                        format="HH:mm"
                                        onChange={() => {
                                            // 当期望揽件开始时间改变时，重新验证期望揽件结束时间
                                            form.validateFields(['expectedPickupTimeEnd']);
                                        }}
                                    />
                                ) : (
                                    <TimePicker 
                                        style={{width: '45%'}} 
                                        format="HH:mm"
                                        disabled
                                    />
                                )}
                            </Form.Item>
                            <Input
                                style={{width: '10%', textAlign: 'center'}}
                                placeholder="~"
                                disabled
                                className="bg-gray-50"
                            />
                            <Form.Item
                                name="expectedPickupTimeEnd"
                                noStyle
                                rules={[
                                    {required: true, message: t('pickup.appointment.messages.pleaseSelectEndTime') as string},
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const startTime = getFieldValue('expectedPickupTimeStart');
                                            if (!value || !startTime) {
                                                return Promise.resolve();
                                            }
                                            if (value.isAfter(startTime)) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Expected pickup end time must be after start time'));
                                        },
                                    }),
                                ]}
                                initialValue={dayjs().hour(18).minute(0).second(0)}
                            >
                                {getFieldEditability('expectedPickupTimeEnd') ? (
                                    <TimePicker style={{width: '45%'}} format="HH:mm"/>
                                ) : (
                                    <TimePicker style={{width: '45%'}} format="HH:mm" disabled/>
                                )}
                            </Form.Item>
                        </Space.Compact>
                    </Form.Item>

                    {/* 循环预约 - 仅在新增模式下显示 */}
                    {mode === 'create' && (
                        <Form.Item
                            name="selectedDates"
                            label={t('pickup.appointment.appointmentPageDetails.fields.cyclicReservation') as string}
                            rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectAppointmentDate') as string}]}
                            initialValue={[safeMinimumPickupDate.format("YYYY-MM-DD")]}
                        >
                            <MultiDateSelector
                                minimumDate={safeMinimumPickupDate}
                                disabled={mode !== 'create'}
                            />
                        </Form.Item>
                    )}

                    {/* 手动加单原因 - 仅创建态或 source=MANUAL 的预约单展示（系统/长期/SLA/复制单不展示） */}
                    {(mode === 'create' || appointment?.source === 'MANUAL') && (
                        <Form.Item
                            name="manualReason"
                            label={t('pickup.appointment.appointmentPageDetails.fields.manualReason') as string}
                            initialValue={initialManualReason || DEFAULT_MANUAL_REASON}
                        >
                            <Select disabled={mode !== 'create'}>
                                {manualReasonOptions.map((option) => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item label={t('pickup.appointment.appointmentPageDetails.fields.expectedParcel')}
                               required={mode === 'create'}>
                        <Space.Compact block>
                            {/* 不设 initialValue：默认值 1 会让必填校验永远满足，用户不填也能提交，
                                落库就是一个没人确认过的 1；后端据「有没有传」标记 expectedParcelCountManual，
                                默认值还会把每张手工单都标成「手动设置过」。留空 + 必填，逼出真实数量。
                                编辑态的值由 setAppointmentData 回填，不依赖 initialValue。 */}
                            {/* 只有新建必填。编辑态不能强制：未手动设置过的单回填为空，
                                若在编辑时也要求必填，运营改个备注就被迫给数量赋值，
                                等于把「跟随挂载运单数」的单人为冻结——正是本需求要避免的。 */}
                            <Form.Item
                                name="expectedParcelCount"
                                noStyle
                                rules={[{
                                    required: mode === 'create',
                                    message: t('pickup.appointment.messages.pleaseSpecifyTheQuantityOfGoods') as string
                                }]}
                            >
                                {getFieldEditability('expectedParcelCount') ? (
                                    <InputNumber style={{width: '70%'}} min={1}
                                                 placeholder={t('pickup.appointment.messages.pleaseSpecifyTheQuantityOfGoods') as string}/>
                                ) : (
                                    <InputNumber style={{width: '70%'}} min={1}
                                                 placeholder={t('pickup.appointment.messages.pleaseSpecifyTheQuantityOfGoods') as string}
                                                 disabled/>
                                )}
                            </Form.Item>
                            <Form.Item
                                name="expectedParcelUnit"
                                noStyle
                                rules={[{required: true, message: t('pickup.appointment.messages.pleaseSelectTheUnitOfQuantity') as string}]}
                                initialValue={undefined}
                            >
                                {getFieldEditability('expectedParcelUnit') ? (
                                    <Select
                                        style={{width: '30%'}}
                                        placeholder={t('pickup.appointment.messages.pleaseSelectTheUnitOfQuantityForTheGoods') as string}
                                        allowClear
                                    >
                                        {packageUnitFilterOptions.map((option) => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                ) : (
                                    <Select
                                        style={{width: '30%'}}
                                        placeholder={t('pickup.appointment.messages.pleaseSelectTheUnitOfQuantityForTheGoods') as string}
                                        allowClear
                                        disabled
                                    >
                                        {packageUnitFilterOptions.map((option) => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>
                        </Space.Compact>
                    </Form.Item>

                    {/* 「预约单自己的车型」是本期新增字段，按 hub 灰度。不渲染时该单的 vehicle_type_id
                        留空，后端自动回退到排期/站点级车型——那条回退链本来就存在，等同改造前行为 */}
                    {startPartialEnabled(warehouse?.id) && (
                        <Form.Item
                            name="vehicleTypeId"
                            label={t('pickup.appointment.columns.vehicleType')}
                        >
                            <Select
                                // 不给 allowClear：保存走的是 PATCH 语义，清空后端只会当成「没传」保持原值，
                                // 留个点了没效果的清除按钮反而误导。要改就选另一个车型。
                                showSearch
                                optionFilterProp="label"
                                options={vehicleTypeOptions}
                                disabled={!getFieldEditability('vehicleTypeId')}
                                placeholder={t('pickup.appointment.messages.vehicleTypeFollowsSite') as string}
                                onChange={() => { vehicleTypeTouchedRef.current = true; }}
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="notes"
                        label={t('pickup.appointment.appointmentPageDetails.fields.remark')}
                        initialValue=""
                    >
                        {getFieldEditability('notes') ? (
                            <TextArea rows={3} placeholder={t('pickup.appointment.messages.pleaseProvideTheRemarksInformation') as string}/>
                        ) : (
                            <TextArea rows={3} placeholder={t('pickup.appointment.messages.pleaseProvideTheRemarksInformation') as string} disabled/>
                        )}
                    </Form.Item>

                    <Form.Item
                        name="pickupSerialNosJson"
                        label={
                            <span className="whitespace-normal break-words text-sm text-gray-800">
                          {t('pickup.appointment.appointmentPageDetails.fields.pickupSerialNos')}
                        </span>
                        }
                        getValueFromEvent={(e) => {
                            // 保存用户输入的文本，不做转换
                            return e.target.value;
                        }}
                        getValueProps={(value) => {
                            // 如果是数组，转换为每行一个的文本显示
                            if (Array.isArray(value)) {
                                return {value: value.join('\n')};
                            }
                            // 如果是字符串，直接显示
                            return {value: value || ''};
                        }}
                        initialValue=""
                    >
                        <TextArea
                            rows={3}
                            placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.pleaseEnterPickupSerialNos') as string}
                            disabled={!getFieldEditability('pickupSerialNosJson')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="trackingNumbersJson"
                        label={
                            <span className="whitespace-normal break-words text-sm text-gray-800">
                          {t('pickup.appointment.appointmentPageDetails.fields.trackingNumbers')}
                        </span>
                        }
                        getValueFromEvent={(e) => {
                            // 保存用户输入的文本，不做转换
                            return e.target.value;
                        }}
                        getValueProps={(value) => {
                            // 如果是数组，转换为每行一个的文本显示
                            if (Array.isArray(value)) {
                                return {value: value.join('\n')};
                            }
                            // 如果是字符串，直接显示
                            return {value: value || ''};
                        }}
                        initialValue=""
                    >
                        <TextArea
                            rows={3}
                            placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.pleaseEnterTrackingNumbers') as string+":\nSWX181110000000000001\nSWX703220000000000002\nSWX511550000000000003"}
                            disabled={!getFieldEditability('trackingNumbersJson')}
                        />
                    </Form.Item>

                    {/* 上门扫描单号：司机上门扫描记录（pickup_order_scan_info），只读，每行一个 */}
                    <Form.Item
                        name="scanTrackingNumbers"
                        label={
                            <span className="whitespace-normal break-words text-sm text-gray-800">
                          {t('pickup.appointment.appointmentPageDetails.fields.scanTrackingNumbers')}
                        </span>
                        }
                        getValueProps={(value) => ({
                            value: Array.isArray(value) ? value.join('\n') : (value || ''),
                        })}
                        initialValue={[]}
                    >
                        <TextArea rows={3} placeholder="--" disabled />
                    </Form.Item>

                    <Form.Item
                        label={t('pickup.appointment.appointmentPageDetails.fields.status')}
                        name="status"
                        initialValue={null}
                    >
                        <Select
                            placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.pleaseSelectTheStatus') as string}
                            disabled
                        >
                            {statusOptions.map((option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={t('pickup.appointment.appointmentPageDetails.fields.exceptionReason')}
                        name="exceptionReasonCode"
                        initialValue={null}
                    >
                        <Select
                            placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.pleaseSelectExceptionReason') as string}
                            allowClear
                            disabled
                            loading={exceptionReasonsLoading}
                            notFoundContent={exceptionReasonsLoading ? 'Loading...' : 'No data'}
                        >
                            {exceptionReasonsData?.getExceptionDictionaryForPickup?.map((option) => (
                                <Select.Option key={option.exceptionReasonCode} value={option.exceptionReasonCode}>
                                    <div className="flex items-center justify-between">
                                        <span>{getExceptionReasonText(option, option.exceptionReasonCode)}</span>
                                        {option.isUploadsImages && (
                                            <span className="text-xs text-blue-500 ml-2">📸</span>
                                        )}
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span className="whitespace-normal break-words text-sm text-gray-800">
                            {t('pickup.appointment.appointmentPageDetails.fields.actualParcel')}
                        </span>
                        }>
                        <Space.Compact block>
                            <Form.Item
                                name="actualParcelCount"
                                noStyle
                                initialValue={0}
                            >
                                <InputNumber
                                    style={{width: '70%'}}
                                    disabled
                                    min={0}
                                    placeholder="0"
                                />
                            </Form.Item>
                            <Form.Item
                                name="actualParcelUnit"
                                noStyle
                                initialValue={undefined}
                            >
                                <Select
                                    style={{width: '30%'}}
                                    disabled
                                    placeholder={t('pickup.appointment.appointmentPageDetails.placeholders.pleaseSelectUnit') as string}
                                    allowClear
                                >
                                    {packageUnitFilterOptions.map((option) => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                        </Space.Compact>
                    </Form.Item>

                    <Form.Item
                        label={
                            appointmentData?.getPickupAppointmentDetail?.isAbnormal 
                                ? t('pickup.appointment.appointmentPageDetails.fields.exceptionPhotos')
                                : t('pickup.appointment.appointmentPageDetails.fields.pickupPhotos')
                        }
                    >
                        <div className="space-y-2">
                            <Form.Item shouldUpdate noStyle>
                                {({ getFieldValue, setFieldValue }) => {
                                    const isAbnormal = appointmentData?.getPickupAppointmentDetail?.isAbnormal;
                                    const fieldName = isAbnormal ? 'exceptionPhotosS3KeysJson' : 'photosS3KeysJson';
                                    const displayUrls = isAbnormal
                                        ? appointmentData?.getPickupAppointmentDetail?.exceptionPhotosS3UrlJson || []
                                        : appointmentData?.getPickupAppointmentDetail?.photosS3UrlJson || [];
                                    const currentKeys = getFieldValue(fieldName) || [];

                                    return (
                                        <ImageUploader
                                            value={currentKeys}
                                            displayUrls={displayUrls}
                                            onChange={(objectKeys) => setFieldValue(fieldName, objectKeys)}
                                            maxCount={6}
                                            disabled={mode !== 'detail'}
                                            tag="pickup-appointment-photo"
                                        />
                                    );
                                }}
                            </Form.Item>
                        </div>
                    </Form.Item>

                    {appointmentData?.getPickupAppointmentDetail?.bolUrl && (
                        <Form.Item label={t('pickup.appointment.appointmentPageDetails.fields.bolDocument')}>
                            {/* BOL 单据为司机端签字后生成的存档快照，只读不可替换 */}
                            {/* 单据是 1:2.5 的竖长图，只约束高度、宽度自适应，点击可放大看细节。
                                圆角要加在外层 .ant-image 上：hover 遮罩是 img 的兄弟节点，img 自己的圆角裁不到它 */}
                            <Image
                                src={appointmentData.getPickupAppointmentDetail.bolUrl}
                                alt="BOL"
                                height={180}
                                rootClassName="overflow-hidden rounded-md border border-gray-300"
                                style={{ display: 'block' }}
                            />
                        </Form.Item>
                    )}

                    {appointmentData?.getPickupAppointmentDetail?.status === 'CANCELLED' && (
                        <Form.Item label={t('pickup.appointment.appointmentPageDetails.fields.cancelVoucher')}>
                            {/* 只有机器人取消展示「机器人自动取消」标签与取消原因（群消息上下文，只读）；人工 / 系统取消与原来一致，取消凭证图片不变 */}
                            {appointmentData.getPickupAppointmentDetail.cancelType === 'BOT' && (
                                <div className="mb-2">
                                    <Tag color="purple" icon={<RobotOutlined />} className="m-0">
                                        {t('pickup.appointment.appointmentPageDetails.fields.botCancelTag')}
                                    </Tag>
                                </div>
                            )}
                            {appointmentData.getPickupAppointmentDetail.cancelType === 'BOT'
                                && appointmentData.getPickupAppointmentDetail.cancelReason && (
                                <div className="mb-2 max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                    {appointmentData.getPickupAppointmentDetail.cancelReason}
                                </div>
                            )}
                            {appointmentData.getPickupAppointmentDetail.cancelType === 'BOT' && (
                                <div className="mb-2 text-xs text-gray-400">
                                    {t('pickup.appointment.appointmentPageDetails.fields.botCancelVoucherHint')}
                                </div>
                            )}
                            <Form.Item shouldUpdate noStyle>
                                {({ getFieldValue, setFieldValue }) => {
                                    const currentKeys = getFieldValue('cancelVoucherS3Keys') || [];
                                    return (
                                        <ImageUploader
                                            value={currentKeys}
                                            displayUrls={appointmentData.getPickupAppointmentDetail.cancelVoucherS3UrlJson || []}
                                            onChange={(objectKeys) => setFieldValue('cancelVoucherS3Keys', objectKeys)}
                                            maxCount={6}
                                            disabled={mode !== 'detail'}
                                            tag="pickup-appointment-cancel-voucher"
                                        />
                                    );
                                }}
                            </Form.Item>
                        </Form.Item>
                    )}
                </Form>
            </div>
        </Drawer>
        <Modal
            title={t('pickup.appointment.cancelTitle')}
            open={cancelDrawerModalVisible}
            okText={t('pickup.appointment.buttons.confirmCancellation')}
            cancelText={t('common.cancel')}
            confirmLoading={cancelLoading}
            onOk={handleConfirmDrawerCancel}
            onCancel={() => setCancelDrawerModalVisible(false)}
        >
            <ImageUploaderEnhance
                value={cancelDrawerVoucherS3Keys}
                onChange={(keys) => { setCancelDrawerVoucherS3Keys(keys); setCancelDrawerVoucherError(false); }}
                maxCount={6}
                tag="pickup-appointment-cancel-voucher"
            />
            {cancelDrawerVoucherError && (
                <div className="text-red-500 text-sm mt-1">{t('pickup.appointment.cancelVoucherRequired')}</div>
            )}
        </Modal>
        </>
    );
};

export default PickupAppointmentDrawer; 
